import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  FormGroup,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreDialog from "../../../app/core/components/CoreDialog/CoreDialog";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreMaxCharacter from "../../../app/core/components/CoreMaxCharacter/CoreMaxCharacter";
import CoreTextArea from "../../../app/core/components/CoreTextArea/CoreTextArea";
import { validateFiscalCode } from "../../../utils/functions/form";
import {
  useDeleteDocumentMutation,
  useEditDocumentMutation,
} from "../../api/admin/gestisciDocumentiApi";

const fieldTypeTranslate = (number) => {
  switch (number) {
    case 1:
      return "date";
    case 2:
      return "number";
    case "DATE":
      return "date";
    default:
      return "string";
  }
};

const getValueFromValueObject = (value) => {
  if (typeof value === "string") {
    if (value === "true") return t("Suitable");
    if (value === "false") return t("NotSuitable");
    return value;
  }

  if (typeof value === "object" && value !== null) {
    if (value.Bool === true) return t("Suitable");
    if (value.Bool === false) return t("NotSuitable");

    return (
      Object.values(value).find(
        (v) =>
          (typeof v === "boolean" && v === true) ||
          (v && (typeof v !== "string" || !v.includes("0001")))
      ) || ""
    );
  }

  return "";
};

const DynamicForm = ({
  formDataFromProps,
  document,
  mainCategory,
  subCategory,
  closeModal,
  handleOpenDiscardDialog,
  handleChangeStatusDoc,
  changeStatus,
  userIsDichCongiunta,
  fiscalCodes,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    extractedDocument: "",
    operatorNotes: "",
  });
  const [errorMessages, setErrorMessages] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [CFMessage, setCFMessage] = useState({});

  const [editDocument, { isLoading: isEditLoading }] =
    useEditDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteDocumentMutation();

  const handleClickPopper = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (["name", "operatorNotes"].includes(name)) {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
      return;
    }
    if (formData.extractedDocument?.fields) {
      const updatedFields = formData.extractedDocument.fields.map((field) => {
        if (field.fieldName === name) {
          return {
            ...field,
            value:
              typeof field.value === "object"
                ? { ...field.value, String: value }
                : value,
          };
        }
        return field;
      });
      setFormData((prevData) => ({
        ...prevData,
        extractedDocument: {
          ...prevData.extractedDocument,
          fields: updatedFields,
        },
      }));
    }
    if (
      ["cf", "codice fiscale"].some((substring) =>
        name.toLowerCase().includes(substring)
      )
    ) {
      const message = validateFiscalCode(
        value,
        document,
        fiscalCodes,
        userIsDichCongiunta
      );
      setCFMessage((prev) => ({ ...prev, [name]: message }));
    }
  };

  const confirmDelete = () => {
    deleteDocument(document.id)
      .then((res) => {
        if (res.data.Result === 1) {
          toast.success(t("DocDeleted"));
          setOpenDeleteDialog(false);
          closeModal();
        } else {
          toast.error(t("DocNotDeleted"));
        }
      })
      .catch((err) => toast.error(err));
  };

  const handleDelete = () => {
    handleOpenDeleteDialog();
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleEdit = () => {
    const id = toast.loading(t("Saving..."), {
      type: "info",
    });
    const bodyData = {
      id: document.id,
      mainCategoryId: mainCategory,
      categoryId: subCategory,
      name: formData.name,
      extractedDocument: JSON.stringify(formData.extractedDocument),
      operatorNotes: formData.operatorNotes || "",
    };
    if (document.status === 3) {
      changeStatus({
        id: document.id,
        status: 5,
      });
    }
    editDocument(bodyData)
      .unwrap()
      .then((data) => {
        if (data.Result !== 1)
          return toast.update(id, {
            render: data.Exception || t("DocNotEdited"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
        toast.update(id, {
          render: t("DocEdited"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
        closeModal();
      })
      .catch(() => {
        toast.update(id, {
          render: t("DocNotEdited"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      extractedDocument: formDataFromProps,
      name: document.name,
      operatorNotes: document.operatorNotes,
    }));
    let messages = [];
    formDataFromProps?.fields?.forEach((e) => {
      if (
        ["cf", "codice fiscale"].some((substring) =>
          e.fieldName.toLowerCase().includes(substring)
        )
      ) {
        const message = validateFiscalCode(
          e.value.String || e.value,
          document,
          fiscalCodes,
          userIsDichCongiunta
        );
        setCFMessage((prev) => ({ ...prev, [e.fieldName]: message }));
        messages.push(message);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDataFromProps, fiscalCodes]);

  useEffect(() => {
    const errors = [];

    // Aggiungere errori CFMessage se presenti
    Object.values(CFMessage).forEach((msg) => {
      if (msg.alert && !msg.positive) {
        errors.push(msg.text);
      }
    });

    setErrorMessages(errors);
  }, [CFMessage, formData.operatorNotes, t]);

  const campiLiberi = formData.extractedDocument?.fields?.filter((e) =>
    /^campo[_ ]libero/i.test(e.fieldName.trim())
  );

  function formatLabel(text) {
    if (!text) return "";
    return text
      .toLowerCase()
      .replaceAll("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return (
    <div className="h-full flex flex-col justify-between">
      {errorMessages.length > 0 && (
        <Alert severity="error" className="mt-2">
          {errorMessages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </Alert>
      )}
      <div className="h-5/6 overflow-scroll">
        {formData.extractedDocument?.fields?.find(
          (e) => e.fieldName.trim().toLowerCase() === "warning_frontend_hidden"
        ) && (
          <div className="w-full flex pt-2">
            <Tooltip title="Warning">
              <IconButton onClick={handleClickPopper}>
                <WarningAmberRoundedIcon className="text-yellow-500" />
              </IconButton>
            </Tooltip>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              elevation={4}
            >
              <div
                className="bg-yellow-500 overflow-auto"
                style={{
                  maxWidth: "700px",
                  minHeight: "60px",
                  maxHeight: "200px",
                  boxShadow: "none",
                }}
              >
                {formData.extractedDocument?.fields?.map(
                  (e) =>
                    e.fieldName.trim().toLowerCase() ===
                      "warning_frontend_hidden" && (
                      <p className="p-4 m-0 text-white">
                        {"- " +
                          (typeof e.value === "object"
                            ? e.value?.String
                            : e.value)}
                      </p>
                    )
                )}
              </div>
            </Popover>
          </div>
        )}

        <div className="flex flex-col">
          {formData.extractedDocument?.fields?.find(
            (e) => e.fieldName.trim().toLowerCase() === "riassunto"
          ) && (
            <>
              <h3>Riassunto documento</h3>
              <div className="w-full max-h-32 overflow-auto rounded-md shadow-md p-4 mb-4">
                <Typography variant="body1">
                  {formData.extractedDocument?.fields?.find(
                    (e) => e.fieldName.trim().toLowerCase() === "riassunto"
                  )?.value?.String ||
                    formData.extractedDocument?.fields?.find(
                      (e) => e.fieldName.trim().toLowerCase() === "riassunto"
                    )?.value}
                </Typography>
              </div>
            </>
          )}
          {campiLiberi && campiLiberi.length > 0 && (
            <>
              <h3>Ulteriori info</h3>
              <div className="w-full max-h-32 overflow-auto rounded-md shadow-md p-4 mb-4">
                {campiLiberi.map((campo, index) => (
                  <Typography key={index} variant="body1">
                    <li>{campo.value?.String || campo.value}</li>
                  </Typography>
                ))}
              </div>
            </>
          )}

          <div className="flex flex-col">
            <h3>
              {t("Data")} {t("document")}
            </h3>
          </div>
          <div className="flex flex-wrap justify-center rounded-md pb-12">
            <div className="w-1/2 p-2">
              <CoreInputField
                className="w-full"
                InputLabelProps={{
                  shrink: formData.name,
                }}
                multiline
                name="name"
                label={`${t("Name")} ${t("document")}`}
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            {formData.extractedDocument?.fields
              ?.filter(
                (e) =>
                  !["riassunto", "warning_frontend_hidden"].includes(
                    e.fieldName.trim().toLowerCase()
                  ) && !/^campo[_ ]libero/i.test(e.fieldName.trim())
              )
              .filter((e, index, self) => {
                // remove duplicates
                if (e.fieldName.trim().toLowerCase() === "classificazione_v2") {
                  return (
                    self.findIndex(
                      (el) =>
                        el.fieldName.trim().toLowerCase() ===
                        "classificazione_v2"
                    ) === index
                  );
                }
                return true;
              }) // remove duplicates
              .filter((e) => {
                if (
                  ["eligibile 730", "rigo e codice del 730"].includes(
                    e.fieldName?.trim().toLowerCase()
                  )
                ) {
                  return (
                    (e.value?.String && e.value.String !== "") ||
                    (e.value?.Bool !== null && e.value.Bool !== false)
                  );
                }

                return true;
              })
              ?.map((e, index) => (
                <div key={index} className="w-1/2 p-2">
                  <CoreInputField
                    multiline
                    className="w-full"
                    InputLabelProps={{
                      shrink:
                        fieldTypeTranslate(e.fieldType) === "date" ||
                        getValueFromValueObject(e.value),
                    }}
                    type={fieldTypeTranslate(e.fieldType)}
                    name={e.fieldName}
                    label={formatLabel(e.fieldName)}
                    value={
                      e.fieldType === 1 || e.fieldType === "DATE"
                        ? getValueFromValueObject(e.value)?.substring(0, 10) ||
                          ""
                        : getValueFromValueObject(e.value)
                    }
                    onChange={handleInputChange}
                    error={
                      CFMessage[e.fieldName] &&
                      !CFMessage[e.fieldName]?.positive
                    }
                    helperText={
                      CFMessage[e.fieldName] && (
                        <p
                          className={`m-0 ${
                            CFMessage[e.fieldName]?.positive
                              ? "text-green-700"
                              : "text-red-500"
                          }`}
                        >
                          {CFMessage[e.fieldName]?.text.split(",")[0]}
                          <br></br>
                          {CFMessage[e.fieldName]?.text.split(",")[1]}
                        </p>
                      )
                    }
                    color={
                      CFMessage[e.fieldName] &&
                      (CFMessage[e.fieldName]?.positive ? "success" : "error")
                    }
                  />
                </div>
              ))}
          </div>

          {document.status === 6 ? (
            <div className="py-4 flex flex-col">
              <CoreTextArea
                disabled
                rows={5}
                label={t("DiscardNotes")}
                name="notesDiscard"
                value={document.rejectionNotes}
              />
            </div>
          ) : (
            <div className="flex gap-4 items-center">
              <FormGroup className="w-full my-5 italic">
                <CoreTextArea
                  disabled
                  rows={5}
                  label={t("UserNotes")}
                  value={document.notes || t("NoOne")}
                />
              </FormGroup>

              <FormGroup className="w-full my-5">
                <CoreTextArea
                  rows={5}
                  label={t("OperatorNotes")}
                  name="operatorNotes"
                  value={formData.operatorNotes}
                  onChange={handleInputChange}
                  error={formData.operatorNotes?.length > 500}
                />
              </FormGroup>
              <CoreMaxCharacter
                maxCharacter={500}
                text={formData.operatorNotes}
              />
            </div>
          )}
        </div>
      </div>
      <div
        style={{ borderTop: "1px solid lightgrey" }}
        className="h-1/6 flex justify-around items-center"
      >
        <div>
          <CoreButton
            onClick={handleDelete}
            testo={t("Delete")}
            className="btn-round bg-red-700 text-white border-red-700"
          />
        </div>

        {document.status !== 6 && (
          <div>
            <CoreButton
              onClick={handleOpenDiscardDialog}
              testo={t("Discard")}
              className="btn-round-outline border-orange-400 text-orange-400"
            />
          </div>
        )}
        <div>
          <CoreButton
            className=" btn-round"
            disabled={isEditLoading || formData.operatorNotes?.length > 500}
            testo={
              document.status === 6
                ? t("Recover")
                : document.status === 3
                ? t("Validate")
                : t("Save")
            }
            onClick={
              document.status === 6
                ? () => handleChangeStatusDoc(document)
                : handleEdit
            }
          />
        </div>
      </div>
      <CoreDialog
        open={openDeleteDialog}
        title={t("DocDeleting")}
        content={`${t("DocDeletingConfirm")} "${formData.name}"?`}
        positiveButton={t("Yes")}
        negativeButton={t("No")}
        handlePositive={confirmDelete}
        closeDialog={handleCloseDeleteDialog}
        disabledButton={isDeleting}
      />
    </div>
  );
};

export default DynamicForm;
